/* eslint eqeqeq: "off", curly: "error", @typescript-eslint/no-unused-vars: "off", react-hooks/exhaustive-deps: "off" */
import { useEffect, useRef, useState } from 'react';
import { AfbService } from '../../../../services/AfbService';
import { CommonService } from '../../../../services/CommonService';
import { ChangeAlertCallbackType } from '../../../Root';
import { SimulationRecord } from '../AfbFeeSimulation';

export const useAfbFeeSimulation = (changeAlert: ChangeAlertCallbackType, changeLoader: (v: boolean) => void) => {
    const afbService = useRef<AfbService>();
    const commonService = useRef<CommonService>();

    // ─── Filter state ───────────────────────────────────────────────────────────
    const [selectedPeriod, setSelectedPeriod] = useState<string>('');
    const [segments, setSegments] = useState<{ k: string; v: string }[]>([]);
    const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

    // ─── Simulation history ──────────────────────────────────────────────────────
    const [simulationRecords, setSimulationRecords] = useState<SimulationRecord[]>([]);
    const [pendingRecord, setPendingRecord] = useState<SimulationRecord | null>(null);
    const [loading, setLoading] = useState(false);

    // ─── Single fee simulation/charge ────────────────────────────────────────────
    const [singleCustomerUuid, setSingleCustomerUuid] = useState<string>('');
    const [singleStrategy, setSingleStrategy] = useState<string>('afb.min.amount.fee');

    // ─── Execution detail ────────────────────────────────────────────────────────
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailData, setDetailData] = useState<any>(null);
    const [detailRecord, setDetailRecord] = useState<SimulationRecord | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ─── Modal ───────────────────────────────────────────────────────────────────
    const [confirmOpen, setConfirmOpen] = useState(false);

    // ─── Widget config (viene en data.widget[0].config de afbExecutionList) ──────
    const [config, setConfig] = useState<any>({});

    // ─── Derived ─────────────────────────────────────────────────────────────────
    const canSimulate = selectedSegments.length > 0 && selectedPeriod !== '';
    const canSingle = singleCustomerUuid.trim() !== '' && singleStrategy !== '';

    // ─── Init ────────────────────────────────────────────────────────────────────
    useEffect(() => {
        afbService.current = new AfbService(changeAlert);
        commonService.current = new CommonService(changeAlert);
        loadSegments();
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        setSelectedPeriod(`${y}-${m}`);
    }, []);

    // ─── Load segments catalog ───────────────────────────────────────────────────
    const loadSegments = async () => {
        setLoading(true);
        try {
            const data = {
                catalog: {
                    id: 'customer_segments',
                    filters: [{ 'metadata.external.origin': 'afb' }],
                }
            };
            await Promise.all([
                commonService.current.getCatalog(data),
                afbService.current.afbExecutionList({})
            ]).then(([response, getList]) => {
                let cat = [];
                if (response?.data?.response === 'OK') {
                    cat = response.data.catalog ?? [];
                    setSegments(Array.isArray(cat) ? cat : []);
                }

                if (getList?.data?.response === 'OK') {
                    setConfig(getList.data.widget?.[0]?.config ?? {});
                    const records = getList.data.content?.executions ?? [];
                    const validatedRecords = validateSegments(records, cat);
                    setSimulationRecords(validatedRecords);
                }
            });
        } catch (e) {
            console.error('Ocurrió un error al obtener los segmentos', e);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setLoading(true);
        try {
            const getList = await afbService.current.afbExecutionList({});
            if (getList?.data?.response === 'OK') {
                setConfig(getList.data.widget?.[0]?.config ?? {});
                const records = getList.data.content?.executions ?? [];
                const validatedRecords = validateSegments(records);
                setSimulationRecords(validatedRecords);
            }
        } catch (e) {
            console.error('Error loading segments catalog', e);
        } finally {
            setLoading(false);
        }
    };

    // ─── Validate Segments ──────────────────────────────────────────────────────
    const validateSegments = (records: SimulationRecord[], cat: any[] = segments) => {
        const lookup = Object.fromEntries(
            cat.map(({ k, v }) => [k, v])
        );

        const result = records.map(item => {
            const segmentsStr = item.segments_to_process.map(p => p === 'single_customer' ? 'Cliente único' : (lookup[p] ?? p));
            return { ...item, segments_to_process_str: segmentsStr };
        });

        return result;
    };

    // ─── Toggle segment selection ────────────────────────────────────────────────
    const toggleSegment = (key: string) => {
        setSelectedSegments(prev =>
            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
        );
    };

    // ─── Simulate ────────────────────────────────────────────────────────────────
    const handleSimulate = async () => {
        if (!canSimulate) { return; }
        setLoading(true);
        try {
            const payload = {
                data: {
                    period: selectedPeriod,
                    simulation: true,
                    segments_to_process: selectedSegments,
                }
            };
            const response = await afbService.current.afbFeeSimulateBatch(payload);
            if (response?.data?.response === 'OK') {
                changeAlert(true, 'success', 'Simulación en proceso, recibirás una notificación al finalizar.');
            }
        } catch (e) {
            console.error('Error simulating fees', e);
        } finally {
            setLoading(false);
        }
    };

    // ─── Single simulate / charge ────────────────────────────────────────────────
    const handleSingleFee = async (simulation: boolean) => {
        if (!canSingle) { return; }
        setLoading(true);
        try {
            const payload = {
                data: {
                    simulation: simulation,
                    customer_uuid: singleCustomerUuid.trim(),
                    tax_policy_type: singleStrategy,
                }
            };
            const response = simulation
                ? await afbService.current.afbFeeSimulateSingle(payload)
                : await afbService.current.afbFeeChargeSingle(payload);
            if (response?.data?.response === 'OK') {
                changeAlert(true, 'success', simulation ? 'Simulación individual exitosa' : 'Cobro individual ejecutado correctamente');
                await handleRefresh();
            } else {
                changeAlert(true, 'error', response?.data?.message ?? 'Error al procesar el fee individual');
            }
        } catch (e) {
            console.error('Error processing single fee', e);
        } finally {
            setLoading(false);
        }
    };

    // ─── Download Excel (report-url) ─────────────────────────────────────────────
    const handleDownloadExcel = async (record: SimulationRecord) => {
        if (!record?.uuid || !record?.metadata?.file_uuid) {
            changeAlert(true, 'error', 'La ejecución no tiene un reporte disponible para descargar.');
            return;
        }

        setLoading(true);
        try {
            const response = await afbService.current.afbFeeDownload({
                data: { execution_id: record.uuid }
            });

            if (response?.data?.response === 'OK') {
                const downloadUrl = response.data.content?.download_url;
                if (!downloadUrl) {
                    changeAlert(true, 'error', 'No se pudo obtener la URL del reporte.');
                    return;
                }

                const link = document.createElement('a');
                link.href = downloadUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = `reporte_fee_${record.uuid}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                const errorCode = response?.data?.code;
                const errorMessage = response?.data?.message || response?.message || '';
                const isReportNotReady = /report[_\s-]?not[_\s-]?ready/i.test(String(errorCode ?? '')) || /report[_\s-]?not[_\s-]?ready/i.test(String(errorMessage));

                changeAlert(
                    true,
                    'error',
                    isReportNotReady
                        ? 'El reporte aún no está listo'
                        : (errorMessage || 'No se pudo descargar el reporte de fee.')
                );
            }
        } catch (e) {
            console.error('Error downloading fee report', e);
            changeAlert(true, 'error', 'No se pudo descargar el reporte de fee.');
        } finally {
            setLoading(false);
        }
    };

    // ─── Execution details ──────────────────────────────────────────────────
    const fetchExecutionDetail = async (record: SimulationRecord | null, pageNumber: number) => {
        if (!record) { return; }
        setDetailLoading(true);
        try {
            const payload = {
                execution_id: record.uuid,
                pagination: {
                    results_per_page: 50,
                    page_number: pageNumber
                }
            };
            const response = await afbService.current.afbExecutionDetail(payload);
            if (response?.data?.response === 'OK') {
                setDetailData(response.data.content ?? null);
            }
        } catch (e) {
            console.error('Error getting execution details', e);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleExecutionDetails = (record: SimulationRecord) => {
        setDetailRecord(record);
        setDetailOpen(true);
        setDetailData(null);
        fetchExecutionDetail(record, 1);
    };

    const closeExecutionDetails = () => {
        setDetailOpen(false);
        setDetailData(null);
        setDetailRecord(null);
    };

    const handleDetailPageChange = (page: number) => {
        fetchExecutionDetail(detailRecord, page);
    };

    // ─── Real charge ─────────────────────────────────────────────────────────────
    const openConfirmModal = (record: SimulationRecord) => {
        setPendingRecord(record);
        setConfirmOpen(true);
    };
    const closeConfirmModal = () => {
        setConfirmOpen(false);
        setPendingRecord(null);
    };

    const handleRealCharge = async () => {
        if (!pendingRecord) { return; }
        setConfirmOpen(false);
        setLoading(true);

        try {
            // Si la ejecución es de cliente único → cobro single; si no → cobro batch
            const isSingle = (pendingRecord.segments_to_process || []).indexOf('single_customer') > -1;

            let customerUuid = (pendingRecord as any).customer_uuid;
            let taxPolicyType = (pendingRecord as any).tax_policy_type ?? (pendingRecord as any).fee_type;

            // El listado de ejecuciones no trae customer_uuid en single:
            // se obtiene del detalle de la ejecución.
            if (isSingle && !customerUuid) {
                const detailResp = await afbService.current.afbExecutionDetail({
                    execution_id: pendingRecord.uuid,
                    pagination: { results_per_page: 1, page_number: 1 }
                });
                const detail = detailResp?.data?.content?.details?.[0];
                customerUuid = detail?.customer_uuid;
                taxPolicyType = taxPolicyType ?? detail?.fee_type;
            }

            const singlePayload = {
                simulation: false,
                customer_uuid: customerUuid,
                tax_policy_type: taxPolicyType,
            };
            const batchPayload = { segments_to_process: pendingRecord.segments_to_process };
            const response = isSingle
                ? await afbService.current.afbFeeChargeSingle(singlePayload)
                : await afbService.current.afbFeeChargeBatch(batchPayload);
            if (response?.data?.response === 'OK') {
                changeAlert(true, 'success', 'Cobro real ejecutado correctamente');
                // Mark the matching record as cobrado
                handleRefresh();
            } else {
                changeAlert(true, 'error', response?.data?.message ?? 'Error al ejecutar el cobro');
            }
        } catch (e) {
            console.error('Error executing real charge', e);
        } finally {
            setPendingRecord(null);
            setLoading(false);
        }
    };

    return {
        // State
        selectedPeriod, setSelectedPeriod,
        segments,
        selectedSegments, setSelectedSegments, toggleSegment,
        simulationRecords,
        pendingRecord,
        loading,
        confirmOpen,
        // Config del widget (actions)
        config,
        // Derived
        canSimulate,
        canSingle,
        // Single
        singleCustomerUuid, setSingleCustomerUuid,
        singleStrategy, setSingleStrategy,
        handleSingleFee,
        // Handlers
        handleSimulate,
        handleDownloadExcel,
        openConfirmModal,
        closeConfirmModal,
        handleRealCharge,
        handleRefresh,
        handleExecutionDetails,
        detailOpen,
        detailData,
        detailLoading,
        closeExecutionDetails,
        handleDetailPageChange
    };
};
